import api from './api';

export type PrestacaoAnexo = {
  id: string;
  prestacaoId: string;
  nomeOriginal: string;
  mimeType?: string;
  tamanhoBytes?: number;
  descricao?: string;
  usuarioUpload?: string;
  dataUpload?: string;
  viewUrl?: string;
  // Publicacao no portal (TAEP D.3), decidida pela prefeitura.
  publicavel?: boolean;
  redacaoConfirmada?: boolean;
  descricaoPublica?: string;
  publicacaoRevisadaPor?: string;
  publicacaoRevisadaEm?: string;
};

export type PrestacaoComprovante = {
  fonteRecurso?: string;
  numeroTransferencia?: string;
  dataPagamento?: string;
  despesaCombustivel?: boolean;
  id: string;
  planoTrabalhoId: string;
  pcId: string;
  valor: number;
  observacoes?: string;
  usuarioCriacao?: string;
  dataCriacao?: string;
  anexos: PrestacaoAnexo[];
  // Anexo 5 (Relatório de Pagamentos Realizados) — todos opcionais; comprovantes
  // lançados antes desta funcionalidade não têm esses dados preenchidos.
  credorNome?: string;
  credorDocumento?: string;
  numeroDocumentoFiscal?: string;
  tipoDocumentoFiscal?: string;
  dataDocumentoFiscal?: string;
  formaTransferencia?: string;
  bancoTransferencia?: string;
  agenciaTransferencia?: string;
  contaTransferencia?: string;
  // Etapa (item) a que este comprovante se refere — undefined quando é um comprovante geral da PC.
  itemExecucaoId?: string;
  itemTitulo?: string;
};

export type PrestacaoConta = {
  id?: string;
  planoTrabalhoId: string;
  pcId?: string;
  valorExecutado?: number;
  observacoes?: string;
  dataCriacao?: string;
  dataAtualizacao?: string;
  finalizada?: boolean;
  dataEnvio?: string;
  usuarioCriacao?: string;
};

/** Cronograma de desembolso (agenda de repasse) do plano — não é mais a unidade da prestação de contas. */
export type PrestacaoParcelaDesembolso = {
  parcelaId: string;
  planoTrabalhoId: string;
  mes: string;
  valor: number;
  dataPagamento?: string;
  numeroProtocolo?: string;
  podeRegistrarRepasse: boolean;
};

/** PC (peça de prestação de contas) — unidade central: única ou parcial, numerada, com protocolo próprio. */
export type PrestacaoPc = {
  pcId: string;
  planoTrabalhoId: string;
  tipo: 'UNICA' | 'PARCIAL';
  numero: number;
  mes?: string;
  parcelaDesembolsoId?: string;
  protocolo: string;
  prazoPrestacaoOriginal?: string;
  prazoPrestacaoFinal?: string;
  vencida: boolean;
  prazoExpirado: boolean;
  diasRestantes?: number | null;
  aguardandoRepasse: boolean;
  ehFinal: boolean;
  podeMarcarFinal: boolean;
  podeProrrogar: boolean;
  podeAdicionarComprovante: boolean;
  statusPrestacao: string;
  valorRecebido: number;
  comprovantes: PrestacaoComprovante[];
  ajusteSolicitadoEm?: string;
  ajusteSolicitadoPor?: string;
  ajusteSolicitadoMensagem?: string;
  ajustePendente: boolean;
  podeSolicitarAjuste: boolean;
  podeResolverAjuste: boolean;
  situacao: PrestacaoPcSituacao;
  correcoesSolicitadas: number;
  limiteCorrecoes: number;
  podeEnviarParaAnalise: boolean;
  pendenciasEnvio: string[];
  podeDecidir: boolean;
  podeSolicitarCorrecao: boolean;
  enviadaAnaliseEm?: string;
  decisaoEm?: string;
  decisaoPor?: string;
  decisaoParecer?: string;
  exigeDeclaracaoDespesas: boolean;
  declaraDespesaRh?: boolean | null;
  declaraDespesaAluguel?: boolean | null;
};

export type PrestacaoPcSituacao =
  | 'PENDENTE'
  | 'RASCUNHO'
  | 'EM_ANALISE'
  | 'APROVADO'
  | 'APROVADO_COM_RESSALVA'
  | 'EM_CORRECAO'
  | 'REPROVADO';

export type PrestacaoPcDecisao = 'APROVAR' | 'APROVAR_COM_RESSALVA' | 'SOLICITAR_CORRECAO' | 'REPROVAR';

export type PrestacaoAjusteSolicitacao = {
  pcId: string;
  planoTrabalhoId: string;
  planoTitulo?: string;
  instituicaoNome?: string;
  tipo?: string;
  numero?: number;
  mensagem?: string;
  solicitadoEm: string;
  solicitadoPor?: string;
};

export type PrestacaoPlanoResumo = {
  planoId: string;
  planoStatus: string;
  disponivel: boolean;
  prazoFinalPlano?: string;
  prazoFinalExpirado: boolean;
  alertaPrazoFinal?: string;
  tipoPc?: 'UNICA' | 'PARCIAL' | null;
  podeCriarPcUnica: boolean;
  podeCriarPcParcial: boolean;
  pcs: PrestacaoPc[];
  parcelasDesembolso: PrestacaoParcelaDesembolso[];
};

const base = '/prestacao-contas';

const normalizeComprovante = (comprovante: any, planoId: string): PrestacaoComprovante => ({
  fonteRecurso: comprovante?.fonteRecurso,
  numeroTransferencia: comprovante?.numeroTransferencia,
  dataPagamento: comprovante?.dataPagamento,
  despesaCombustivel: comprovante?.despesaCombustivel,
  id: comprovante?.id ?? '',
  planoTrabalhoId: comprovante?.planoTrabalhoId ?? planoId,
  pcId: comprovante?.pcId ?? '',
  valor: Number(comprovante?.valor ?? comprovante?.valorExecutado ?? 0),
  observacoes: comprovante?.observacoes,
  usuarioCriacao: comprovante?.usuarioCriacao,
  dataCriacao: comprovante?.dataCriacao,
  anexos: Array.isArray(comprovante?.anexos) ? comprovante.anexos : [],
  credorNome: comprovante?.credorNome,
  credorDocumento: comprovante?.credorDocumento,
  numeroDocumentoFiscal: comprovante?.numeroDocumentoFiscal,
  tipoDocumentoFiscal: comprovante?.tipoDocumentoFiscal,
  dataDocumentoFiscal: comprovante?.dataDocumentoFiscal,
  formaTransferencia: comprovante?.formaTransferencia,
  bancoTransferencia: comprovante?.bancoTransferencia,
  agenciaTransferencia: comprovante?.agenciaTransferencia,
  contaTransferencia: comprovante?.contaTransferencia,
  itemExecucaoId: comprovante?.itemExecucaoId,
  itemTitulo: comprovante?.itemTitulo,
});

const normalizePc = (pc: any, planoId: string): PrestacaoPc => ({
  pcId: pc?.pcId ?? '',
  planoTrabalhoId: pc?.planoTrabalhoId ?? planoId,
  tipo: pc?.tipo === 'UNICA' ? 'UNICA' : 'PARCIAL',
  numero: Number(pc?.numero ?? 0),
  mes: pc?.mes,
  parcelaDesembolsoId: pc?.parcelaDesembolsoId,
  protocolo: pc?.protocolo ?? '',
  prazoPrestacaoOriginal: pc?.prazoPrestacaoOriginal,
  prazoPrestacaoFinal: pc?.prazoPrestacaoFinal,
  vencida: Boolean(pc?.vencida),
  prazoExpirado: Boolean(pc?.prazoExpirado),
  diasRestantes: pc?.diasRestantes ?? null,
  aguardandoRepasse: Boolean(pc?.aguardandoRepasse),
  ehFinal: Boolean(pc?.ehFinal),
  podeMarcarFinal: Boolean(pc?.podeMarcarFinal),
  podeProrrogar: Boolean(pc?.podeProrrogar),
  podeAdicionarComprovante: Boolean(pc?.podeAdicionarComprovante),
  statusPrestacao: pc?.statusPrestacao ?? '',
  valorRecebido: Number(pc?.valorRecebido ?? 0),
  ajusteSolicitadoEm: pc?.ajusteSolicitadoEm,
  ajusteSolicitadoPor: pc?.ajusteSolicitadoPor,
  ajusteSolicitadoMensagem: pc?.ajusteSolicitadoMensagem,
  ajustePendente: Boolean(pc?.ajustePendente),
  podeSolicitarAjuste: Boolean(pc?.podeSolicitarAjuste),
  podeResolverAjuste: Boolean(pc?.podeResolverAjuste),
  situacao: pc?.situacao ?? 'PENDENTE',
  correcoesSolicitadas: Number(pc?.correcoesSolicitadas ?? 0),
  limiteCorrecoes: Number(pc?.limiteCorrecoes ?? 3),
  podeEnviarParaAnalise: Boolean(pc?.podeEnviarParaAnalise),
  pendenciasEnvio: Array.isArray(pc?.pendenciasEnvio) ? pc.pendenciasEnvio : [],
  podeDecidir: Boolean(pc?.podeDecidir),
  podeSolicitarCorrecao: Boolean(pc?.podeSolicitarCorrecao),
  enviadaAnaliseEm: pc?.enviadaAnaliseEm,
  decisaoEm: pc?.decisaoEm,
  decisaoPor: pc?.decisaoPor,
  decisaoParecer: pc?.decisaoParecer,
  exigeDeclaracaoDespesas: Boolean(pc?.exigeDeclaracaoDespesas),
  declaraDespesaRh: pc?.declaraDespesaRh ?? null,
  declaraDespesaAluguel: pc?.declaraDespesaAluguel ?? null,
  comprovantes: Array.isArray(pc?.comprovantes)
    ? pc.comprovantes.map((c: any) => normalizeComprovante(c, planoId))
    : [],
});

const normalizeParcelaDesembolso = (parcela: any, planoId: string): PrestacaoParcelaDesembolso => ({
  parcelaId: parcela?.parcelaId ?? '',
  planoTrabalhoId: parcela?.planoTrabalhoId ?? planoId,
  mes: parcela?.mes ?? '',
  valor: Number(parcela?.valor ?? 0),
  dataPagamento: parcela?.dataPagamento,
  numeroProtocolo: parcela?.numeroProtocolo,
  podeRegistrarRepasse: Boolean(parcela?.podeRegistrarRepasse),
});

const normalizeResumo = (data: any): PrestacaoPlanoResumo => {
  const planoId = data?.planoId ?? '';
  const pcs = Array.isArray(data?.pcs) ? data.pcs : [];
  const parcelasDesembolso = Array.isArray(data?.parcelasDesembolso) ? data.parcelasDesembolso : [];

  return {
    planoId,
    planoStatus: data?.planoStatus ?? '',
    disponivel: Boolean(data?.disponivel),
    prazoFinalPlano: data?.prazoFinalPlano,
    prazoFinalExpirado: Boolean(data?.prazoFinalExpirado),
    alertaPrazoFinal: data?.alertaPrazoFinal,
    tipoPc: data?.tipoPc ?? null,
    podeCriarPcUnica: Boolean(data?.podeCriarPcUnica),
    podeCriarPcParcial: Boolean(data?.podeCriarPcParcial),
    pcs: pcs.map((pc: any) => normalizePc(pc, planoId)),
    parcelasDesembolso: parcelasDesembolso.map((p: any) => normalizeParcelaDesembolso(p, planoId)),
  };
};

const getResumo = async (planoId: string): Promise<PrestacaoPlanoResumo> => {
  const res = await api.get(`${base}/plano/${planoId}`);
  return normalizeResumo(res.data);
};

const getResumos = async (planoIds: string[]): Promise<PrestacaoPlanoResumo[]> => {
  if (planoIds.length === 0) return [];
  const res = await api.post(`${base}/planos/resumos`, { planoIds });
  return Array.isArray(res.data) ? res.data.map(normalizeResumo) : [];
};

const listByPlano = async (planoId: string): Promise<PrestacaoConta[]> => {
  const resumo = await getResumo(planoId);
  return (Array.isArray(resumo.pcs) ? resumo.pcs : []).flatMap((pc) =>
    pc.comprovantes.map((comprovante) => ({
      id: comprovante.id,
      planoTrabalhoId: comprovante.planoTrabalhoId,
      pcId: comprovante.pcId,
      valorExecutado: comprovante.valor,
      observacoes: comprovante.observacoes,
      dataCriacao: comprovante.dataCriacao,
      dataAtualizacao: comprovante.dataCriacao,
      finalizada: false,
      usuarioCriacao: comprovante.usuarioCriacao,
    })),
  );
};

// ── PC -- criação e ciclo de vida ──────────────────────────────────────────

const prorrogarPrazo = async (pcId: string, planoId: string, payload: { novoPrazo: string }) => {
  const res = await api.post(`${base}/pcs/${pcId}/prorrogar`, payload);
  return normalizePc(res.data, planoId);
};

/** Convenios/Admin notificam o Operador de que a PC precisa de ajustes. */
const solicitarAjuste = async (pcId: string, planoId: string, mensagem: string) => {
  const res = await api.post(`${base}/pcs/${pcId}/solicitar-ajuste`, { mensagem });
  return normalizePc(res.data, planoId);
};

const enviarParaAnalise = async (pcId: string, planoId: string) => {
  const res = await api.post(`${base}/pcs/${pcId}/enviar-analise`);
  return normalizePc(res.data, planoId);
};

const decidirPc = async (pcId: string, planoId: string, decisao: PrestacaoPcDecisao, parecer?: string) => {
  const res = await api.post(`${base}/pcs/${pcId}/decisao`, { decisao, parecer });
  return normalizePc(res.data, planoId);
};

const ajustarLimiteCorrecoes = async (pcId: string, planoId: string, limite: number) => {
  const res = await api.put(`${base}/pcs/${pcId}/limite-correcoes`, { limite });
  return normalizePc(res.data, planoId);
};

/** Operador/Admin marcam o ajuste solicitado como resolvido. */
const resolverAjuste = async (pcId: string, planoId: string) => {
  const res = await api.post(`${base}/pcs/${pcId}/resolver-ajuste`);
  return normalizePc(res.data, planoId);
};

/** PCs com ajuste pendente, para a area de trabalho do usuario. */
const getAjustesPendentes = async (): Promise<PrestacaoAjusteSolicitacao[]> => {
  const res = await api.get(`${base}/pcs/ajustes-pendentes`);
  return Array.isArray(res.data) ? res.data : [];
};

// ── cronograma de desembolso (repasse) ─────────────────────────────────────

// ── comprovantes (Anexo 5) ──────────────────────────────────────────────────

export type PrestacaoComprovantePagamento = {
  fonteRecurso?: string;
  numeroTransferencia?: string;
  dataPagamento?: string;
  despesaCombustivel?: boolean;
  credorNome?: string;
  credorDocumento?: string;
  numeroDocumentoFiscal?: string;
  tipoDocumentoFiscal?: string;
  dataDocumentoFiscal?: string;
  formaTransferencia?: string;
  bancoTransferencia?: string;
  agenciaTransferencia?: string;
  contaTransferencia?: string;
};

const criarComprovante = async (
  pcId: string,
  // TEMPORARIO: file passou a aceitar null -- anexo desabilitado a pedido do usuario
  // (sessao 2026-09-18). Reverter para File obrigatorio junto com o backend.
  payload: { valor: number; observacoes?: string; file: File | null; comprovanteDespesa?: File | null; controleAbastecimento?: File | null; itemExecucaoId?: string } & PrestacaoComprovantePagamento,
) => {
  const formData = new FormData();
  formData.append('valor', String(payload.valor));
  if (payload.observacoes) formData.append('observacoes', payload.observacoes);
  if (payload.itemExecucaoId) formData.append('itemExecucaoId', payload.itemExecucaoId);
  if (payload.file) formData.append('file', payload.file);
  if (payload.comprovanteDespesa) formData.append('comprovanteDespesa', payload.comprovanteDespesa);
  if (payload.controleAbastecimento) formData.append('controleAbastecimento', payload.controleAbastecimento);
  if (payload.fonteRecurso) formData.append('fonteRecurso', payload.fonteRecurso);
  if (payload.numeroTransferencia) formData.append('numeroTransferencia', payload.numeroTransferencia);
  if (payload.dataPagamento) formData.append('dataPagamento', payload.dataPagamento);
  formData.append('despesaCombustivel', String(Boolean(payload.despesaCombustivel)));
  if (payload.credorNome) formData.append('credorNome', payload.credorNome);
  if (payload.credorDocumento) formData.append('credorDocumento', payload.credorDocumento);
  if (payload.numeroDocumentoFiscal) formData.append('numeroDocumentoFiscal', payload.numeroDocumentoFiscal);
  if (payload.tipoDocumentoFiscal) formData.append('tipoDocumentoFiscal', payload.tipoDocumentoFiscal);
  if (payload.dataDocumentoFiscal) formData.append('dataDocumentoFiscal', payload.dataDocumentoFiscal);
  if (payload.formaTransferencia) formData.append('formaTransferencia', payload.formaTransferencia);
  if (payload.bancoTransferencia) formData.append('bancoTransferencia', payload.bancoTransferencia);
  if (payload.agenciaTransferencia) formData.append('agenciaTransferencia', payload.agenciaTransferencia);
  if (payload.contaTransferencia) formData.append('contaTransferencia', payload.contaTransferencia);

  const res = await api.post(`${base}/pcs/${pcId}/comprovantes`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as PrestacaoComprovante;
};

const deletarComprovante = async (comprovanteId: string) => {
  await api.delete(`${base}/comprovantes/${comprovanteId}`);
};

/** Corrige a fonte declarada de um pagamento de etapa (1 = Prefeitura, 2 = recurso próprio). */
const alterarFonteRecurso = async (comprovanteId: string, fonteRecurso: '1' | '2'): Promise<PrestacaoComprovante> => {
  const res = await api.put(`${base}/comprovantes/${comprovanteId}/fonte-recurso`, { fonteRecurso });
  return res.data;
};

const definirPublicacaoAnexo = async (
  anexoId: string,
  payload: { publicavel: boolean; descricaoPublica?: string; redacaoConfirmada: boolean },
): Promise<PrestacaoAnexo> => {
  const res = await api.put(`${base}/anexos/${anexoId}/publicacao`, payload);
  return res.data;
};

const getComprovantesPorParcela = async (pcId: string): Promise<PrestacaoComprovante[]> => {
  const res = await api.get(`${base}/pcs/${pcId}/comprovantes`);
  return Array.isArray(res.data) ? res.data : [];
};

export type PrestacaoPcModalidade = 'UNICA' | 'PARCIAL';
export type PrestacaoPcPeriodicidade = 'BIMESTRAL' | 'QUADRIMESTRAL';

export type PrestacaoPcConfig = {
  planoTrabalhoId: string;
  modalidade?: PrestacaoPcModalidade | null;
  periodicidade?: PrestacaoPcPeriodicidade | null;
  vigenciaInicio?: string | null;
  vigenciaFim?: string | null;
  vigenciaDias?: number | null;
  podeEditar: boolean;
  pcsGeradas: boolean;
  periodos: { numero: number; inicio: string; fim: string; prazo: string; ehFinal: boolean }[];
};

const getConfiguracaoPcs = async (planoId: string): Promise<PrestacaoPcConfig> => {
  const res = await api.get(`${base}/pcs/plano/${planoId}/configuracao`);
  return res.data;
};

const salvarConfiguracaoPcs = async (
  planoId: string,
  payload: { modalidade: PrestacaoPcModalidade; periodicidade?: PrestacaoPcPeriodicidade | null },
): Promise<PrestacaoPcConfig> => {
  const res = await api.put(`${base}/pcs/plano/${planoId}/configuracao`, payload);
  return res.data;
};

export default {
  enviarParaAnalise,
  decidirPc,
  ajustarLimiteCorrecoes,
  getConfiguracaoPcs,
  salvarConfiguracaoPcs,
  listByPlano,
  getResumo,
  getResumos,
  prorrogarPrazo,
  criarComprovante,
  deletarComprovante,
  alterarFonteRecurso,
  getComprovantesPorParcela,
  definirPublicacaoAnexo,
  solicitarAjuste,
  resolverAjuste,
  getAjustesPendentes,
  create: async (_payload?: Partial<PrestacaoConta>) => {
    throw new Error('Use o fluxo de prestação por PC para inserir comprovantes.');
  },
  update: async (_id: string, _payload?: Partial<PrestacaoConta>) => {
    throw new Error('Edite a prestação de contas diretamente pela PC.');
  },
  remove: async (id: string) => {
    await deletarComprovante(id);
  },
};
